import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MatrixRain } from './components/MatrixRain/MatrixRain';
import { Navbar } from './components/Navbar';
import { Hero } from './components/home/Hero';
import { VideoSection } from './components/home/VideoSection';
import { TwoCoreOffers } from './components/home/TwoCoreOffers';
import { HowItWorks } from './components/home/HowItWorks';
import { WhoIWorkWith } from './components/home/WhoIWorkWith';
import { WhatIActuallyDo } from './components/home/WhatIActuallyDo';
import { StartHereForm } from './components/home/StartHereForm';
import { BookingFlow } from './components/booking/BookingFlow';
import { AboutPage } from './pages/AboutPage';
import { BookAuditPage } from './pages/BookAuditPage';
import { ContactPage } from './pages/ContactPage';
import { ThankYouPage } from './pages/ThankYouPage';
import { ContactThankYouPage } from './pages/ContactThankYouPage';
import { GuideThankYouPage } from './pages/GuideThankYouPage';
import { BookingThankYouPage } from './pages/BookingThankYouPage';
import { ServiceDetailsPage } from './pages/ServiceDetailsPage';
import { BusinessModelsPage } from './pages/BusinessModelsPage';
import { LeadRescueSystemPage } from './pages/LeadRescueSystemPage';
import { ProfilePage } from './pages/ProfilePage';
import { MyServicesPage } from './pages/MyServicesPage';
import { MarketingPage } from './pages/MarketingPage';
import { AutomationPage } from './pages/AutomationPage';
import { OtherServicesPage } from './pages/OtherServicesPage';
import { PricingPage } from './pages/PricingPage';
import {
  PrivacyPolicyPage,
  TermsOfServicePage,
  FAQPage,
  SupportMaintenancePage,
  RefundPolicyPage
} from './pages/PolicyPages';
import { Footer } from './components/Footer';
import { ScrollToTop } from './components/common/ScrollToTop';
import { useUserStore } from './stores/userStore';
import { supabase } from './lib/supabase';
import { scrollToStartHere } from './utils/navigation';

export default function App() {
  const fetchProfile = useUserStore((state) => state.fetchProfile);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        fetchProfile();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="min-h-screen bg-black text-gray-300 relative">
        <div className="absolute inset-0 overflow-hidden">
          <MatrixRain />
        </div>
        <div className="relative z-10">
          <Navbar />
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/marketing" element={<MarketingPage />} />
              <Route path="/automation" element={<AutomationPage />} />
              <Route path="/other-services" element={<OtherServicesPage />} />
              <Route path="/lead-rescue-system" element={<LeadRescueSystemPage />} />
              <Route path="/book-audit" element={<BookAuditPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/thank-you" element={<ThankYouPage />} />
              <Route path="/contact/thank-you" element={<ContactThankYouPage />} />
              <Route path="/guide/thank-you" element={<GuideThankYouPage />} />
              <Route path="/booking/thank-you" element={<BookingThankYouPage />} />
              <Route path="/services/:id" element={<ServiceDetailsPage />} />
              <Route path="/book" element={<BookingFlow />} />
              <Route path="/business-models" element={<BusinessModelsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/my-services" element={<MyServicesPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/support-maintenance" element={<SupportMaintenancePage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </div>
    </BrowserRouter>
  );
}

function HomePage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#start-here') {
      scrollToStartHere(300);
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>Priority AI Services - Simple Systems for Health Clinics</title>
        <meta
          name="description"
          content="Marketing and lead rescue systems for osteopathy, physio, massage, and med spa clinics. Get clients while you're with patients—without chasing leads or managing social media."
        />
        <link rel="canonical" href="https://priorityaiservices.ca" />
      </Helmet>
      <Hero />
      <VideoSection />
      <TwoCoreOffers />
      <HowItWorks />
      <WhoIWorkWith />
      <WhatIActuallyDo />
      <StartHereForm />
    </>
  );
}